import React from 'react';
import { Link } from 'react-router-dom';
import { Play, Pause, Heart, Download, MoreVertical, CheckCircle } from 'lucide-react';
import { usePlayer } from '../context/PlayerContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const SongCard = ({ song, playlist = [], showArtist = true, compact = false }) => {
  const { currentSong, isPlaying, playSong, pauseSong } = usePlayer();
  const isCurrentSong = currentSong?.id === song.id;

  const handlePlayClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isCurrentSong && isPlaying) {
      pauseSong();
    } else {
      playSong(song, playlist);
    }
  };

  const defaultCover = `https://images.unsplash.com/photo-1581650127213-e72e2271ff15?w=400&h=400&fit=crop`;

  return (
    <div
      className="group bg-[#16181D] rounded-2xl p-3 transition-all duration-300 hover:bg-[#1E2028] hover:-translate-y-1"
      data-testid={`song-card-${song.id}`}
    >
      {/* Cover Image */}
      <div className="relative aspect-square mb-3 overflow-hidden rounded-xl bg-[#0A0B0F]">
        <img
          src={song.cover_image || defaultCover}
          alt={song.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />

        {/* Play Button Overlay */}
        <button
          onClick={handlePlayClick}
          className="absolute bottom-2 right-2 h-11 w-11 rounded-full bg-[#FFE500] text-black flex items-center justify-center shadow-lg shadow-[#FFE500]/30 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
          data-testid={`play-btn-${song.id}`}
        >
          {isCurrentSong && isPlaying ? (
            <Pause className="h-5 w-5" fill="black" />
          ) : (
            <Play className="h-5 w-5 ml-0.5" fill="black" />
          )}
        </button>

        {/* Free/Paid Badge */}
        {!song.is_free && (
          <div className="absolute top-2 left-2 bg-[#FFE500] text-black text-[11px] font-bold px-2.5 py-0.5 rounded-full">
            ₹{song.price}
          </div>
        )}
      </div>

      {/* Song Info */}
      <div className="px-1">
        <Link
          to={`/song/${song.id}`}
          className="block font-semibold text-white hover:text-[#FFE500] transition-colors line-clamp-1 text-sm"
          data-testid={`song-title-${song.id}`}
        >
          {song.title}
        </Link>

        {showArtist && (
          <Link
            to={`/${song.artist_name?.toLowerCase().replace(/\s+/g, '-')}`}
            className="flex items-center gap-1 text-xs text-[#A0A0A0] hover:text-white transition-colors mt-0.5"
          >
            <span className="truncate">{song.artist_name}</span>
            {song.is_verified && <CheckCircle className="h-3 w-3 text-[#FFE500] flex-shrink-0" />}
          </Link>
        )}

        {!compact && (
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1 text-[11px] text-[#666]">
              <Play className="h-3 w-3" />
              {song.play_count?.toLocaleString() || 0}
            </div>

            <div className="flex items-center gap-1">
              <button
                className="like-btn h-7 w-7 flex items-center justify-center text-[#666] hover:text-red-500 transition-colors"
                data-testid={`like-btn-${song.id}`}
              >
                <Heart className="h-3.5 w-3.5" />
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="h-7 w-7 flex items-center justify-center text-[#666] hover:text-white transition-colors">
                    <MoreVertical className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#16181D] border-[#1E2028] text-white rounded-xl">
                  <DropdownMenuItem className="cursor-pointer focus:bg-[#1E2028] focus:text-[#FFE500]">
                    <Heart className="h-4 w-4 mr-2" />
                    Add to Favorites
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer focus:bg-[#1E2028] focus:text-[#FFE500]">
                    Add to Playlist
                  </DropdownMenuItem>
                  {!song.is_free && (
                    <DropdownMenuItem className="cursor-pointer focus:bg-[#1E2028] focus:text-[#FFE500]">
                      <Download className="h-4 w-4 mr-2" />
                      Purchase Download
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem className="cursor-pointer focus:bg-[#1E2028] focus:text-[#FFE500]">
                    Share
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SongCard;
